/**
 * AuthContext — port of the well-engineered pattern from the legacy app
 * (reference/insolech-web-app-current/src/contexts/AuthContext.tsx).
 *
 * Loads session + roles + clinic membership on mount; refresh on auth state
 * change. AuthGate (App.tsx) waits for both to be resolved before rendering
 * routes, preventing flash-of-wrong-dashboard.
 */

import * as React from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { clearSubmitIntent } from '@/features/orders/wizard/submitIntent';

export type AppRole = 'admin' | 'agent' | 'podiatrist' | 'provider' | 'home_services_manager';

/** A clinic the current user belongs to (joined from clinic_members → clinics). */
export interface UserClinic {
  id: string;
  name: string;
  role: 'owner' | 'member';
}

interface AuthState {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  /**
   * True while we resolve the user's profile + roles + clinic membership
   * after the AuthGate has released. Phase 4b iteration's "release-early"
   * pattern lets the route tree mount as soon as `getSession()` resolves;
   * however, `clinicId` is only set later by `resolveProfileAndRole`. Pages
   * that branch on `clinicId == null` (Dashboard, OrderTracker, PatientsList)
   * MUST wait on `profileLoading` before deciding the user "is not linked
   * to a clinic" — otherwise they false-positive during the resolution
   * window. See Phase 4b iter round 2 fix D.
   */
  profileLoading: boolean;
  /**
   * Set of every role granted to the current user.
   *
   * Source of truth for role-aware UI. The four boolean flags below
   * (`isAdmin`, `isProvider`, `isPodiatrist`, `isAgent`) are membership
   * checks against this set, so a user with multiple roles (e.g. a
   * podiatrist who is also an agent for an introduced clinic) gets
   * `isPodiatrist === true` AND `isAgent === true` simultaneously.
   *
   * Empty set means "no role assigned yet" (the user has just signed up
   * but the admin has not granted them a role).
   */
  roles: ReadonlySet<AppRole>;
  /** Convenience accessor, equivalent to `roles.has(role)`. */
  hasRole: (role: AppRole) => boolean;
  /**
   * Priority-flattened single role used for "which dashboard to land on"
   * decisions. Priority: admin > provider > podiatrist > agent.
   *
   * Note: this is NOT a "has role X" check. For that, use `isAdmin`,
   * `isProvider`, `isPodiatrist`, `isAgent`, or `hasRole`. The rationale
   * is that a podiatrist + agent should land on their podiatrist
   * workspace (their primary surface) and visit `/agent` as a secondary
   * pill, not bounce to `/agent` on first load.
   */
  userRole: AppRole | null;
  /**
   * True once `resolveProfileAndRole` has completed at least once for
   * the current session. AuthGate uses this (together with session
   * presence) to decide when it is safe to mount the route tree.
   */
  rolesLoaded: boolean;
  /**
   * @deprecated Prefer `defaultClinicId` (same value, clearer name) or
   * `clinics` for multi-clinic UI. Kept as a shim so the existing call
   * sites keep working through the multi-clinic rollout.
   */
  clinicId: string | null;
  /**
   * Default clinic for new actions. Equals `profiles.clinic_id` when set,
   * otherwise the first joined clinic membership, otherwise null.
   * Use this when opening the new-order wizard or creating a patient.
   */
  defaultClinicId: string | null;
  /**
   * All active clinics the user belongs to (from clinic_members joined with
   * clinics for display). Empty array while loading or for users with no
   * memberships. Length > 1 unlocks the at-action clinic pickers in the UI.
   */
  clinics: UserClinic[];
  /** Display name from `profiles.full_name`, fallback to email prefix. */
  fullName: string | null;
  /** Public URL of the user's avatar, or null if none uploaded. */
  avatarUrl: string | null;
  /**
   * Bug-fix B: when TRUE this user (typically also an agent) earns
   * commission AND sees in /agent the orders they themselves placed as
   * podiatrist on clinics they acquired. Default FALSE on the DB side;
   * defaults to FALSE here too while profileLoading is still true.
   * Admin-toggled per user from /admin/users.
   */
  agentIncludesOwnOrders: boolean;
  isAdmin: boolean;
  isProvider: boolean;
  isPodiatrist: boolean;
  isAgent: boolean;
  /** Read/track access to the Home Services admin section only (not full admin). */
  isHomeServicesManager: boolean;
  signOut: () => Promise<void>;
  /** Re-run profile + role + clinic membership queries (e.g. after the
   *  user updates their name/avatar in /settings). */
  refreshProfile: () => Promise<void>;
}

const AuthContext = React.createContext<AuthState | null>(null);

/**
 * Auth events that should trigger a fresh profile/role/clinic resolve.
 * Deliberately EXCLUDES:
 *   - INITIAL_SESSION: fires right after mount, but the useEffect's own
 *     getSession() path below already triggers a resolve. Re-resolving
 *     here would double every cold-mount fetch.
 *   - TOKEN_REFRESHED: fires on tab-return and on the periodic auto-refresh
 *     interval. The token rotated but the profile/clinic/role data did not,
 *     so re-fetching just flips profileLoading=true and shows a loading
 *     skeleton ("Loading your clinic...") for no functional benefit. Worse,
 *     if any of the three PostgREST calls stalls past the 6s per-call
 *     timeout, the profileWatchdog fires and the page renders the empty
 *     "not yet linked" state. See tab-return flicker fix.
 */
const RESOLVE_EVENTS = new Set(['SIGNED_IN', 'USER_UPDATED']);

const PROFILE_CACHE_STORAGE_KEY = 'insolech-profile-cache-v1';
const PROFILE_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface CachedProfile {
  userId: string;
  roles: AppRole[];
  clinics: UserClinic[];
  defaultClinicId: string | null;
  fullName: string | null;
  avatarUrl: string | null;
  agentIncludesOwnOrders: boolean;
  cachedAt: number;
}

function readProfileCache(userId: string): CachedProfile | null {
  try {
    const raw = window.localStorage.getItem(PROFILE_CACHE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedProfile;
    if (parsed.userId !== userId) return null;
    const now = Date.now();
    if (now - parsed.cachedAt > PROFILE_CACHE_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeProfileCache(payload: CachedProfile): void {
  try {
    window.localStorage.setItem(
      PROFILE_CACHE_STORAGE_KEY,
      JSON.stringify(payload),
    );
  } catch {
    /* ignore quota / private browsing */
  }
}

function clearProfileCache(): void {
  try {
    window.localStorage.removeItem(PROFILE_CACHE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<Session | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  // Phase 4b iter round 2 (Fix D): tracks whether resolveProfileAndRole has
  // completed since the last sign-in / mount. Initial state is `true` because
  // we ALWAYS need to resolve profile data on cold mount before pages can
  // safely check `clinicId == null`. Flipped to `false` in resolveProfileAndRole's
  // `finally` block (so it always flips even on PostgREST error), or eagerly
  // when there's no session (logged-out users don't need to load profile).
  const [profileLoading, setProfileLoading] = React.useState(true);
  // Source of truth for role-aware UI. A user with multiple roles
  // (e.g. podiatrist + agent) gets every role in this set; the booleans
  // exposed by the context are `roles.has(...)` checks.
  const [roles, setRoles] = React.useState<ReadonlySet<AppRole>>(() => new Set());
  const [rolesLoaded, setRolesLoaded] = React.useState(false);
  const [defaultClinicId, setDefaultClinicId] = React.useState<string | null>(null);
  const [clinics, setClinics] = React.useState<UserClinic[]>([]);
  const [fullName, setFullName] = React.useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null);
  // Bug-fix B: profiles.agent_includes_own_orders mirror. Drives the
  // /agent surface's "exclude my own orders" gate and informs the
  // commission engine indirectly (the DB trigger reads the same column).
  const [agentIncludesOwnOrders, setAgentIncludesOwnOrders] = React.useState(false);

  const resolveProfileAndRole = React.useCallback(async (userId: string) => {
    // NOTE: we intentionally do NOT setProfileLoading(true) here. With the
    // localStorage cache (PROFILE_CACHE_STORAGE_KEY) hydrating state on cold
    // mount, flipping back to loading would cause a flicker on every
    // background refresh. The initial useState(true) handles the first-ever
    // cold mount; the `finally` block below always flips to false.
    try {
      // Per-call instrumentation (Task A1): wrap each PostgREST call so we
      // know which one is slow when the watchdog fires. Logs to console.warn
      // when a call exceeds 1500ms or throws, returning the original result.
      async function timed<T>(name: string, fn: () => Promise<T>): Promise<T> {
        const start = performance.now();
        try {
          const result = await fn();
          const ms = Math.round(performance.now() - start);
          if (ms > 1500) console.warn('[AuthContext] slow ' + name + ' ' + ms + 'ms');
          return result;
        } catch (e) {
          const ms = Math.round(performance.now() - start);
          console.warn('[AuthContext] failed ' + name + ' ' + ms + 'ms', e);
          throw e;
        }
      }

      // Per-call timeout fallback (Task A2): Promise.race each call against a
      // 6s timer; on timeout we resolve with a sentinel "empty" result of the
      // correct shape so Promise.all always completes and the call sites
      // (which already handle empty data) render their not-yet-linked
      // fallbacks instead of hanging on a skeleton.
      function withTimeout<T>(name: string, fn: () => Promise<T>, fallback: T, ms = 6000): Promise<T> {
        return new Promise<T>((resolve) => {
          let settled = false;
          const t = window.setTimeout(() => {
            if (settled) return;
            settled = true;
            console.warn('[AuthContext] timeout ' + name + ' >' + ms + 'ms; using fallback');
            resolve(fallback);
          }, ms);
          fn()
            .then((v) => {
              if (settled) return;
              settled = true;
              window.clearTimeout(t);
              resolve(v);
            })
            .catch((e) => {
              if (settled) return;
              settled = true;
              window.clearTimeout(t);
              console.warn('[AuthContext] failed ' + name, e);
              resolve(fallback);
            });
        });
      }

      const [rolesRes, profileRes, memberRes] = await Promise.all([
        timed('user_roles', () =>
          withTimeout(
            'user_roles',
            async () =>
              supabase
                .from('user_roles')
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .select('role' as any)
                .eq('user_id', userId),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            { data: [], error: null, __timedOut: true } as unknown as any,
          ),
        ),
        timed('profiles', () =>
          withTimeout(
            'profiles',
            async () =>
              supabase
                .from('profiles')
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .select('full_name, clinic_id, avatar_url, agent_includes_own_orders' as any)
                .eq('id', userId)
                .maybeSingle(),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            { data: null, error: null, __timedOut: true } as unknown as any,
          ),
        ),
        // R35 — pull the joined clinic name so the at-action clinic picker
        // can render `${name}` (not a raw uuid) without an extra query.
        // The inner select uses PostgREST's relation syntax to follow the
        // `clinic_id` FK to `clinics.name`.
        timed('clinic_members', () =>
          withTimeout(
            'clinic_members',
            async () =>
              supabase
                .from('clinic_members')
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .select('clinic_id, role, is_active, joined_at, clinics(name)' as any)
                .eq('user_id', userId)
                .order('joined_at', { ascending: true, nullsFirst: false }),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            { data: [], error: null, __timedOut: true } as unknown as any,
          ),
        ),
      ]);

      // Short-circuit guard: if ANY of the three calls hit the 6s withTimeout
      // fallback (or returned a real PostgREST error), do NOT overwrite the
      // hydrated cache state with empty values. Otherwise a transient stall
      // on tab-return would wipe roles/clinics, hide the Agent nav pill, and
      // render the "not yet linked" copy until the user reloads. The finally
      // block still flips profileLoading=false.
      const anyTimedOut =
        (rolesRes as { __timedOut?: boolean }).__timedOut === true ||
        (profileRes as { __timedOut?: boolean }).__timedOut === true ||
        (memberRes as { __timedOut?: boolean }).__timedOut === true;

      const anyErrored = !!(rolesRes.error || profileRes.error || memberRes.error);

      if (anyTimedOut || anyErrored) {
        console.warn(
          '[AuthContext] resolveProfileAndRole: timeout or error; preserving cached state',
          {
            anyTimedOut,
            anyErrored,
            rolesError: rolesRes.error,
            profileError: profileRes.error,
            memberError: memberRes.error,
          },
        );
        return;
      }

      if (rolesRes.error) console.warn('Role lookup failed', rolesRes.error.message);
      // user_roles is one-row-per-(user, role) with a UNIQUE constraint, so
      // the raw rows already de-duplicate themselves at the DB layer. We
      // still defensively filter to the AppRole union here so a typo or
      // legacy value in the table (e.g. a string the app does not know
      // about) does not poison the role set.
      const KNOWN_ROLES: ReadonlySet<AppRole> = new Set([
        'admin',
        'agent',
        'podiatrist',
        'provider',
        'home_services_manager',
      ]);
      const rawRoles = ((rolesRes.data ?? []) as unknown as Array<{ role: string }>).map(
        (r) => r.role,
      );
      const resolvedRoles = new Set<AppRole>(
        rawRoles.filter((r): r is AppRole => KNOWN_ROLES.has(r as AppRole)),
      );
      setRoles(resolvedRoles);
      setRolesLoaded(true);

      const profile = (profileRes.data ?? null) as
        | {
            full_name: string | null;
            clinic_id: string | null;
            avatar_url: string | null;
            agent_includes_own_orders: boolean | null;
          }
        | null;
      setFullName(profile?.full_name ?? null);
      setAvatarUrl(profile?.avatar_url ?? null);
      // Default to FALSE when the column is null (column existed before
      // the user's profile row was migrated, or the row is missing).
      setAgentIncludesOwnOrders(profile?.agent_includes_own_orders ?? false);

      const memberships = ((memberRes.data ?? []) as unknown as Array<{
        clinic_id: string;
        role: string;
        is_active: boolean;
        joined_at: string | null;
        // PostgREST returns the related row as an object or array depending on
        // cardinality. Both shapes are handled below.
        clinics: { name: string | null } | Array<{ name: string | null }> | null;
      }>).filter((m) => m.is_active);

      const resolvedClinics: UserClinic[] = memberships.map((m) => {
        const rel = m.clinics;
        const name = Array.isArray(rel)
          ? rel[0]?.name ?? ''
          : rel?.name ?? '';
        return {
          id: m.clinic_id,
          name: name || 'Clinic',
          role: m.role === 'owner' ? 'owner' : 'member',
        };
      });
      setClinics(resolvedClinics);

      // Default clinic: we prefer the explicit `profiles.clinic_id` ONLY
      // when that clinic still appears in the live memberships. If the
      // legacy profile row points at a clinic the user is no longer a
      // member of (renamed, deactivated, or re-seated), we fall through to
      // the first joined membership so the wizard does not boot with a
      // stale clinic that will fail RLS at insert time. Both fall through
      // to null when neither is available (admins, agents, or users not
      // yet linked to any clinic).
      const profileClinic = profile?.clinic_id ?? null;
      const firstClinic = resolvedClinics[0]?.id ?? null;
      const profileClinicIsValid =
        profileClinic != null &&
        resolvedClinics.some((c) => c.id === profileClinic);
      const resolvedDefaultClinicId = profileClinicIsValid ? profileClinic : firstClinic;
      setDefaultClinicId(resolvedDefaultClinicId);

      // Persist the just-resolved profile to localStorage so the next cold
      // mount can hydrate the UI instantly instead of blocking on the
      // Promise.all of three PostgREST calls.
      writeProfileCache({
        userId,
        roles: Array.from(resolvedRoles),
        clinics: resolvedClinics,
        defaultClinicId: resolvedDefaultClinicId,
        fullName: profile?.full_name ?? null,
        avatarUrl: profile?.avatar_url ?? null,
        agentIncludesOwnOrders: profile?.agent_includes_own_orders ?? false,
        cachedAt: Date.now(),
      });
    } finally {
      // Always flip — even on error — so the dashboard never gets stuck on a
      // loading skeleton. If resolution genuinely failed, `clinicId` will be
      // null and the dashboard will correctly fall through to the "not yet
      // linked to a clinic" message.
      setProfileLoading(false);
    }
  }, []);

  React.useEffect(() => {
    let mounted = true;

    // Watchdog — if anything below stalls (e.g. supabase.auth.getSession()
    // hangs on a corrupt localStorage entry or a slow network on cold mount),
    // force-release the AuthGate after 5s so the route tree mounts and the
    // user isn't stuck on FullPageLoader forever. Bug 1 (Phase 4b iteration)
    // diagnosed this as: NO supabase requests fire after hard nav, the
    // getSession() promise never resolved, AuthGate showed spinner forever.
    const watchdog = window.setTimeout(() => {
      if (!mounted) return;
      console.warn('[AuthContext] Watchdog: getSession() did not resolve in 5s — releasing gate.');
      setIsLoading(false);
    }, 5000);

    const release = () => {
      if (!mounted) return;
      window.clearTimeout(watchdog);
      setIsLoading(false);
    };

    // Second watchdog: profileLoading. resolveProfileAndRole is a Promise.all
    // over three PostgREST calls; if ANY one of them hangs (Supabase fetch
    // stalls, WebSocket churn, intermittent network), profileLoading never
    // flips to false and every page that gates on it (dashboard KPIs, the
    // Loading-your-clinic subtitle, the wizard) is stuck on skeletons
    // forever. We can't cancel an in-flight fetch from the client, but we
    // CAN force the UI to move on. After 8s, treat the resolve as failed:
    // clinicId stays null and clinics stays []; the dashboard then renders
    // the "not yet linked" state, which is at least actionable. If the real
    // calls land later, their setState will repopulate the context.
    const profileWatchdog = window.setTimeout(() => {
      if (!mounted) return;
      console.warn(
        '[AuthContext] profileWatchdog: resolveProfileAndRole did not complete in 8s, forcing profileLoading=false. Underlying calls may still be in flight.',
      );
      setProfileLoading(false);
    }, 8000);

    void supabase.auth
      .getSession()
      .then(async ({ data }) => {
        if (!mounted) return;
        setSession(data.session);
        // Release the gate as soon as we know whether there's a session;
        // resolving the profile/role happens in the background and updates
        // role-aware UI when ready. This prevents the entire app from being
        // blocked behind 3 sequential PostgREST queries on every hard nav.
        release();
        if (data.session?.user) {
          // Hydrate from localStorage cache (if any) BEFORE awaiting the
          // background refresh. This lets the UI render real data on cold
          // mount in <1ms instead of waiting for three PostgREST calls; the
          // background resolveProfileAndRole still runs and overwrites state
          // with fresh data, but the user never sees a loading flicker.
          const cached = readProfileCache(data.session.user.id);
          if (cached) {
            setRoles(new Set(cached.roles));
            setRolesLoaded(true);
            setClinics(cached.clinics);
            setDefaultClinicId(cached.defaultClinicId);
            setFullName(cached.fullName);
            setAvatarUrl(cached.avatarUrl);
            setAgentIncludesOwnOrders(cached.agentIncludesOwnOrders);
            setProfileLoading(false);
          }
          try {
            await resolveProfileAndRole(data.session.user.id);
          } catch (e) {
            console.warn('[AuthContext] resolveProfileAndRole failed', e);
            // resolveProfileAndRole's `finally` already flips profileLoading=false,
            // but defend against the case where the call never started.
            if (mounted) setProfileLoading(false);
          }
        } else {
          // No session — the user is logged out. They don't need profile data,
          // so flip profileLoading=false eagerly so the auth page (and any
          // logged-out branches in protected routes) can render without
          // waiting on a non-existent fetch.
          if (mounted) setProfileLoading(false);
        }
      })
      .catch((e) => {
        console.warn('[AuthContext] getSession failed', e);
        release();
        // Even if getSession itself failed, we should not block UI on a
        // profile fetch that never started.
        if (mounted) setProfileLoading(false);
      });

    // Tab-return pre-warm: when the tab regains visibility, fire a session
    // refresh plus a TLS-warming GET to the Supabase auth health endpoint in
    // parallel. This overlaps the auth-js silent refresh with the user's
    // "looking back at the tab" perception window, so by the time React
    // commits the next route change the HTTP/2 connection is warm and
    // PostgREST requests fire immediately instead of stalling ~6s behind a
    // cold-connection refresh. `getSession()` internally triggers
    // `refreshSession()` if the access token is within EXPIRY_MARGIN.
    // The watchdog, per-call timeout, and profile cache from prior commits
    // stay intact as defence-in-depth in case this pre-warm itself stalls.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void supabase.auth.getSession();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (supabaseUrl) {
        void fetch(supabaseUrl + '/auth/v1/health', {
          method: 'GET',
          cache: 'no-store',
          credentials: 'omit',
        }).catch(() => {
          /* swallow, pre-warm only */
        });
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      setSession(newSession);
      if (!newSession?.user) {
        setRoles(new Set());
        setRolesLoaded(false);
        setDefaultClinicId(null);
        setClinics([]);
        setFullName(null);
        setAvatarUrl(null);
        setAgentIncludesOwnOrders(false);
        // No session after auth state change (sign-out, session expired),
        // logged-out users don't need profile data.
        setProfileLoading(false);
        // Evict the localStorage profile cache so the next sign-in (possibly
        // as a different user) hydrates from fresh data, not the previous
        // session's snapshot.
        clearProfileCache();
        // Same for the wizard's resumable-submit intent: it names an order
        // owned by THIS user and must never surface for the next sign-in on
        // a shared machine (defense in depth - loadSubmitIntent also checks
        // ownership).
        clearSubmitIntent();
        return;
      }
      // Skip TOKEN_REFRESHED + INITIAL_SESSION: the in-memory profile state
      // is still valid; re-fetching here would silently pile duplicate
      // PostgREST calls on top of the cold-mount fetch and make every tab
      // return show a loading flicker for no benefit.
      if (!RESOLVE_EVENTS.has(event)) return;
      try {
        await resolveProfileAndRole(newSession.user.id);
      } catch (e) {
        console.warn('[AuthContext] resolveProfileAndRole (onAuthStateChange) failed', e);
        setProfileLoading(false);
      }
    });

    return () => {
      mounted = false;
      window.clearTimeout(watchdog);
      window.clearTimeout(profileWatchdog);
      document.removeEventListener('visibilitychange', onVisible);
      subscription.unsubscribe();
    };
  }, [resolveProfileAndRole]);

  const refreshProfile = React.useCallback(async () => {
    const userId = session?.user?.id;
    if (!userId) return;
    await resolveProfileAndRole(userId);
  }, [session, resolveProfileAndRole]);

  const signOut = React.useCallback(async () => {
    await supabase.auth.signOut({ scope: 'global' });
    setRoles(new Set());
    setRolesLoaded(false);
    setDefaultClinicId(null);
    setClinics([]);
    setFullName(null);
    setAvatarUrl(null);
    setAgentIncludesOwnOrders(false);
    // Logged-out users don't need profile data — flip profileLoading=false so
    // any branch that was waiting on it can render its logged-out fallback.
    setProfileLoading(false);
    // Belt-and-braces — clear any storage left from earlier integrations
    try {
      window.localStorage.removeItem('insolech-auth');
    } catch {
      /* ignore */
    }
    // Evict the localStorage profile cache so the next sign-in (possibly as a
    // different user) hydrates from fresh data, not this session's snapshot.
    clearProfileCache();
    // And the wizard's resumable-submit intent (order ownership is per-user;
    // see submitIntent.ts).
    clearSubmitIntent();
  }, []);

  // Membership-based booleans. A user can hold several roles at once
  // (e.g. podiatrist + agent for an introduced clinic). Each flag is
  // strictly "has this role", they are NOT mutually exclusive.
  const isAdmin = roles.has('admin');
  const isProvider = roles.has('provider');
  const isPodiatrist = roles.has('podiatrist');
  const isAgent = roles.has('agent');
  const isHomeServicesManager = roles.has('home_services_manager');

  // Priority-flattened "primary view" role. Used by RoleHome to pick a
  // landing page and by the AppShell to pick a logo link target. The
  // order (admin > provider > podiatrist > agent) is intentional:
  //   - admin users always manage from the admin shell
  //   - providers' surface is narrow, so when they hold a second role we
  //     still keep them in the provider portal
  //   - podiatrist beats agent so a "podiatrist + agent" combo lands on
  //     the podiatrist dashboard (their primary workspace), with the
  //     Agent page available as a secondary nav pill
  //   - agent-only users land on /agent
  const userRole: AppRole | null =
    isAdmin ? 'admin'
    : isProvider ? 'provider'
    : isPodiatrist ? 'podiatrist'
    : isAgent ? 'agent'
    : null;

  const hasRole = React.useCallback((role: AppRole) => roles.has(role), [roles]);

  const value: AuthState = {
    session,
    user: session?.user ?? null,
    isLoading,
    profileLoading,
    roles,
    hasRole,
    userRole,
    rolesLoaded,
    // R35 — `clinicId` retained as a deprecated alias for `defaultClinicId`
    // so existing callers (dashboard, order tracker, marketplace, etc.)
    // keep working through the multi-clinic rollout.
    clinicId: defaultClinicId,
    defaultClinicId,
    clinics,
    fullName,
    avatarUrl,
    agentIncludesOwnOrders,
    isAdmin,
    isProvider,
    isPodiatrist,
    isAgent,
    isHomeServicesManager,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
